/* globals Vue, AZURE_URI, LOCALE, PROPERTIES_URL, BOOKING_APP_URI, PREFIX */

import { useCurrencyObserver } from '../use-currency-observer'
import AuthService from '../AuthService'
import { getHashParam } from 'scripts/utils-common'

function useBookingParams () {
  const property = Vue.ref(null)
  const destination = Vue.ref(null)
  const startDate = Vue.ref(new Date())
  const nights = Vue.ref(1)
  const adults = Vue.ref(2)
  const children = Vue.ref([])
  const guestsAdjustedWarning = Vue.ref(false)
  const accessCode = Vue.ref('')

  async function checkAccessCodeAsync () {
    accessCode.value = accessCode.value.trim()
    try {
      if (accessCode.value !== '') {
        const response = await fetch(`${AZURE_URI}CheckAccessCode?accessCode=${encodeURIComponent(accessCode.value)}&locale=${LOCALE}`)
        if (!response.ok) {
          throw new Error('Error checking access code.')
        }
        const ids = await response.json()
        const isVoucher = ids.length === 1 && ids[0] === 'voucher'
        const isValid = (property.value ? ids.filter(id => id === (property.value?.phobsId)) : ids).length > 0
        return { isValid, isVoucher }
      }
    } catch (err) {
      console.error('Error checking access code:', err)
      return { isValid: true, isVoucher: false }
    }

    return { isValid: true, isVoucher: false }
  }

  const bookingUrl = Vue.computed(() => {
    let urlQuery = ''

    urlQuery += `date=${startDate.value.toISOString().substring(0, 10)}`

    urlQuery += `&nights=${nights.value}`
    urlQuery += `&adults=${adults.value}`
    urlQuery += `&children=${children.value.map(child => child.age).join(',')}`

    if (accessCode.value !== '') {
      urlQuery += `&accessCode=${encodeURIComponent(accessCode.value)}`
    }

    if (destination.value) {
      urlQuery += `&destination=${encodeURIComponent(destination.value.slug)}`
    }

    if (!property.value) {
      return `${PROPERTIES_URL}?${urlQuery}`
    }

    if (AuthService.loginHint) {
      urlQuery += `&loginHint=${encodeURIComponent(AuthService.loginHint)}`
    }

    urlQuery = `propertyId=${encodeURIComponent(property.value.phobsId)}&${urlQuery}`

    return `${BOOKING_APP_URI}${PREFIX}/step1?${urlQuery}&currency=${useCurrencyObserver().value}`
  })

  function modifyGuestNumbersAccordingToPropertyLimits ({ maxChildAge, maxGuests, maxAdults, maxChildren }) {
    let newAdults = adults.value
    let newChildren = children.value.slice()

    if (maxChildAge) {
      newChildren = newChildren.filter(c => c.age <= maxChildAge)
      newAdults += children.value.length - newChildren.length
    }

    if (newAdults > maxAdults) {
      newAdults = maxAdults
    }

    if (newChildren.length > maxChildren) {
      newChildren.splice(maxChildren)
    }

    if (newAdults + newChildren.length > maxGuests) {
      const diff = newAdults + newChildren.length - maxGuests
      if (diff <= newChildren.length) {
        newChildren.splice(0, diff)
      } else {
        newAdults -= diff - newChildren.length
        newChildren = []
      }
    }

    return { adults: newAdults, children: newChildren }
  }

  function loadDataFromHashParams () {
    console.log('roman test loading params')
    if (getHashParam('startDate') && getHashParam('nights')) {
      startDate.value = new Date(getHashParam('startDate'))
      nights.value = parseInt(getHashParam('nights'))
    }
    if (getHashParam('guests')) {
      const [numAdults, ...childAges] = getHashParam('guests').split(';')
      adults.value = parseInt(numAdults)
      children.value = childAges.map(i => ({ age: parseInt(i) }))
    }
    if (getHashParam('code')) {
      accessCode.value = getHashParam('code')
    }
  }

  Vue.watch(property, () => {
    if (property.value) {
      const modifiedGuests = modifyGuestNumbersAccordingToPropertyLimits(property.value)
      if (JSON.stringify(modifiedGuests) !== JSON.stringify({ adults: adults.value, children: children.value })) {
        adults.value = modifiedGuests.adults
        children.value = modifiedGuests.children
        guestsAdjustedWarning.value = true
      }
    }
  })

  return {
    property,
    destination,
    startDate,
    nights,
    adults,
    children,
    guestsAdjustedWarning,
    accessCode,
    bookingUrl,
    checkAccessCodeAsync,
    loadDataFromHashParams,
  }
}

export { useBookingParams }
